import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminUsers, useApproveUser, useRejectUser, useSuspendUser } from '../../../api/admin';
import { UserStatus } from '@spi/shared';
import { StatusBadge } from '../shared/StatusBadge';
import { Pagination } from '../shared/Pagination';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { CreateUserModal } from './CreateUserModal';
import { UserIcon, OrgIcon } from '../shared/EntityIcons';
import toast from 'react-hot-toast';

export function UserReviewList() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAdminUsers({
    status: (statusFilter || undefined) as UserStatus | undefined,
    search: search || undefined,
    page,
    limit: 20,
  });

  const approve = useApproveUser();
  const reject = useRejectUser();
  const suspend = useSuspendUser();

  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{ type: 'reject' | 'suspend' | 'approve'; userId: number; userName: string } | null>(null);

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { type, userId } = confirmAction;
    try {
      if (type === 'reject') {
        await reject.mutateAsync(userId);
        toast.success('User rejected');
      } else if (type === 'suspend') {
        await suspend.mutateAsync(userId);
        toast.success('User suspended');
      } else if (type === 'approve') {
        await approve.mutateAsync(userId);
        toast.success('User approved');
      }
    } catch {
      toast.error(`Failed to ${type} user`);
    }
    setConfirmAction(null);
  };

  const confirmTitle = confirmAction?.type === 'reject' ? 'Reject User'
    : confirmAction?.type === 'suspend' ? 'Suspend User'
    : 'Approve User';

  const confirmMessage = confirmAction?.type === 'reject'
    ? `Are you sure you want to reject ${confirmAction.userName}? They will not be able to access the marketplace.`
    : confirmAction?.type === 'suspend'
    ? `Are you sure you want to suspend ${confirmAction.userName}? They will lose access until re-approved.`
    : `Are you sure you want to approve ${confirmAction?.userName}? They will gain full marketplace access.`;

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="input max-w-xs text-sm py-2"
        />
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="input max-w-[160px] text-sm py-2"
        >
          <option value="">All Statuses</option>
          <option value={UserStatus.PENDING}>Pending</option>
          <option value={UserStatus.APPROVED}>Approved</option>
          <option value={UserStatus.REJECTED}>Rejected</option>
          <option value={UserStatus.SUSPENDED}>Suspended</option>
        </select>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="btn btn-primary text-sm py-2 px-4 ml-auto"
        >
          + Create User
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {data?.data.map((u) => (
              <div key={u.id} className="card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-spi-charcoal">
                      <UserIcon className="w-4 h-4 inline-block align-text-bottom text-spi-slate mr-1" />
                      <Link to={`/marketplace/users/${u.id}`} className="hover:text-spi-green">{u.firstName} {u.lastName}</Link>
                    </h4>
                    <p className="text-sm text-spi-slate">{u.email}</p>
                    <p className="text-xs text-spi-slate mt-1">
                      {u.organization && (
                        <>
                          <Link to={`/marketplace/organizations/${u.organization.id}`} className="inline-flex items-center gap-0.5 hover:text-spi-green">
                            <OrgIcon className="w-3.5 h-3.5" />{u.organization.name}
                          </Link>
                          {' '}&middot;{' '}
                        </>
                      )}
                      {u.role}{u.organization?.country && <> &middot; {u.organization.country}</>}
                    </p>
                    {u.organization?.description && (
                      <p className="text-xs text-spi-slate mt-1 line-clamp-2">{u.organization.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={u.status} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {u.status === UserStatus.PENDING && (
                    <>
                      <button
                        onClick={() => setConfirmAction({ type: 'approve', userId: u.id, userName: `${u.firstName} ${u.lastName}` })}
                        disabled={approve.isPending}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => setConfirmAction({ type: 'reject', userId: u.id, userName: `${u.firstName} ${u.lastName}` })}
                        disabled={reject.isPending}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {u.status === UserStatus.APPROVED && (
                    <button
                      onClick={() => setConfirmAction({ type: 'suspend', userId: u.id, userName: `${u.firstName} ${u.lastName}` })}
                      disabled={suspend.isPending}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      Suspend
                    </button>
                  )}
                  {u.status === UserStatus.SUSPENDED && (
                    <button
                      onClick={() => setConfirmAction({ type: 'approve', userId: u.id, userName: `${u.firstName} ${u.lastName}` })}
                      disabled={approve.isPending}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                    >
                      Re-Approve
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />}
        </>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmTitle}
        danger={confirmAction?.type === 'reject' || confirmAction?.type === 'suspend'}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
      />

      <CreateUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
    </div>
  );
}
